import { memo, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  TextField,
  FormControlLabel,
  Typography,
  Checkbox,
  MenuItem,
} from "@mui/material";
import KsbDialogActions from "../../../2_ksbHub/components/KsbDialogActions";
import { Autocomplete } from "@mui/material";
import appService from "../../../2_ksbHub/service/app.service";

function AddSonderEventDialog({
  open,
  onClose,
  onSave,
  sonderEventTemplates,
  selectedSchoolyear,
  schoolyears,
  initialData,
}) {
  const [label, setLabel] = useState("");
  const [templateId, setTemplateId] = useState(null);
  const [selectedSchoolyearId, setSelectedSchoolyearId] = useState("");

  const [roles, setRoles] = useState({
    teacher: true,
    student: true,
  });

  const [adminUsers, setAdminUsers] = useState([]);
  const [selectedAdmins, setSelectedAdmins] = useState([]);
  const isEditMode = !!initialData;
  const ROLE_TEACHER = 2;
  const ROLE_STUDENT = 3;

  useEffect(() => {
    const loadUsers = async () => {
      const data = await appService.getUsers();
      setAdminUsers(data);
    };
    loadUsers();
  }, []);

  useEffect(() => {
    if (open) {
      if (initialData) {
        setLabel(initialData.label || "");
        setTemplateId(initialData.templateId ?? null);

        setRoles({
          teacher: initialData.roles?.includes(ROLE_TEACHER),
          student: initialData.roles?.includes(ROLE_STUDENT),
        });

        // ✅ IMPORTANT: set selected admins
        // assumes initialData.admins = array of user objects or IDs
        if (initialData.admins && adminUsers.length > 0) {
          const selected = adminUsers.filter((u) =>
            initialData.admins.includes(u.id),
          );
          setSelectedAdmins(selected);
        }

        setSelectedSchoolyearId(
          selectedSchoolyear != null ? String(selectedSchoolyear.id) : "",
        );
      } else {
        // ✅ reset when creating
        setLabel("");
        setTemplateId(null);
        setRoles({ teacher: true, student: true });
        setSelectedAdmins([]);

        setSelectedSchoolyearId(
          selectedSchoolyear?.id ? String(selectedSchoolyear.id) : "",
        );
      }
    }
  }, [open, initialData, adminUsers, selectedSchoolyear]);

  // ✅ submit handler
  const handleSubmit = () => {
    if (!label.trim()) return;

    if (!selectedSchoolyearId) {
      console.error("No schoolyear selected");
      return;
    }
    const roleIds = [];
    if (roles.teacher) roleIds.push(2);
    if (roles.student) roleIds.push(3);

    onSave({
      id: initialData?.id, // ✅ needed for update
      schoolyearId: Number(selectedSchoolyearId),
      sondertagLabel: label.trim(),
      templateId,
      roles: roleIds,
      admins: selectedAdmins.map((u) => u.id),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      {isEditMode ? "SonderEvent bearbeiten" : "Neuen SonderEvent erstellen"}

      <DialogContent>
        {/* ✅ LABEL INPUT */}
        <TextField
          fullWidth
          label="SonderEventName (z.B. Datum)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          sx={{ mt: 2 }}
        />
        <Autocomplete
          multiple
          options={adminUsers}
          getOptionLabel={(u) =>
            `${u.email}${u.abbreviation ? ` (${u.abbreviation})` : ""}`
          }
          value={selectedAdmins}
          filterSelectedOptions
          onChange={(sondertag, newValue) => setSelectedAdmins(newValue)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField {...params} label="SonderEvent Admins" sx={{ mt: 2 }} />
          )}
        />
        <TextField
          select
          fullWidth
          label="Schuljahr"
          value={selectedSchoolyearId}
          onChange={(e) => {
            setSelectedSchoolyearId(e.target.value);
          }}
          sx={{ mt: 2 }}
        >
          {schoolyears?.map((sy) => (
            <MenuItem key={sy.id} value={String(sy.id)}>
              {sy.label}
            </MenuItem>
          ))}
        </TextField>
        {/* ✅ TEMPLATE SECTION */}
        <Typography variant="subtitle2" sx={{ mt: 3 }}>
          Vorlage auswählen
        </Typography>
        <TextField
          select
          label="Vorlage"
          value={templateId ?? ""}
          onChange={(e) => {
            const value = e.target.value;
            setTemplateId(value ? Number(value) : null);
          }}
          fullWidth
        >
          {sonderEventTemplates?.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.label}
            </MenuItem>
          ))}
          <MenuItem value="">Keine Vorlage benutzen</MenuItem>
        </TextField>
        {/* ✅ ROLE VISIBILITY */}
        <Typography variant="subtitle2" sx={{ mt: 3 }}>
          Sichtbarkeit
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={roles.teacher}
              onChange={(e) =>
                setRoles({ ...roles, teacher: e.target.checked })
              }
            />
          }
          label="Lehrpersonen"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={roles.student}
              onChange={(e) =>
                setRoles({ ...roles, student: e.target.checked })
              }
            />
          }
          label="Lernende"
        />
      </DialogContent>

      <KsbDialogActions
        onCancel={onClose}
        onConfirm={handleSubmit}
        confirmLabel={isEditMode ? "Speichern" : "Erstellen"}
        confirmDisabled={!label.trim() || !selectedSchoolyearId}
      />
    </Dialog>
  );
}

export default memo(AddSonderEventDialog);
